import { useEffect, useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import PlateStartForm from './quick/PlateStartForm';
import { Code, Day, Taxon } from './cells';
import * as S from '../specimens/styles';

/*
 * "+ Add isolate / dish" -> Field collection -> "Select an existing
 * collection": the person's own recent collections, as Field Mode's gallery
 * lists them (GET /collections?mine=1), and for the one chosen the plate
 * form: the two plates a submitted collection waits for (PUT
 * /plates/sample/{id}/activate), or new plates (POST /plates) when none were
 * made. A collection that still has plates says so and leads to its row; a
 * draft is finished in Field Mode first (its submit makes the plates).
 *
 * Props: user, protocols, t (the hub's), onDone(message, collection),
 * onOpenIsolate(code), onCancel, onBusyChange.
 */
export const RECENT = 20;

export default function FieldCollectionPicker({ user, protocols, t, onDone, onOpenIsolate, onCancel, onBusyChange }) {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [chosen, setChosen] = useState(null);
  const [mode, setMode] = useState(null);   // activate | start | has_plates | draft
  const [plates, setPlates] = useState([]);

  useEffect(() => {
    let live = true;
    api.get('/collections', { params: { mine: 1, limit: RECENT } })
      .then((res) => {
        if (!live) return;
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.items || []);
        setRows(list.filter((r) => r && r.sample_id && !r.is_public_submission));
        setState('ready');
      })
      .catch((e) => { if (live) { setError(errorText(e)); setState('failed'); } });
    return () => { live = false; };
  }, []);

  const pick = async (row) => {
    setChosen(row);
    setPlates([]);
    if (row.is_draft) { setMode('draft'); return; }
    setMode('checking');
    try {
      const res = await api.get(`/collections/${row.sample_id}/family`);
      const made = res?.data?.plates || [];
      setPlates(made);
      if (made.some((p) => p.status === 'awaiting_creation')) setMode('activate');
      else if (made.length) setMode('has_plates');
      else setMode('start');
    } catch (e) {
      setError(errorText(e));
      setMode('failed');
    }
  };

  const label = (row) => row.specimen_code || row.qr_uid || row.code || t(`add.field.kind.${row.kind || 'observation'}`);

  if (chosen && (mode === 'activate' || mode === 'start')) {
    const item = { sample_id: chosen.sample_id, specimen_code: chosen.specimen_code || chosen.qr_uid || '',
      taxon_name: chosen.current_taxon_name || null, plates: [] };
    return (
      <div data-testid="field-picker-plates">
        <p style={{ ...S.hint, margin: '0 0 8px' }}>
          <Code strong>{label(chosen)}</Code>
          {chosen.current_taxon_name && <> · <Taxon name={chosen.current_taxon_name} /></>}
          {chosen.collection_datetime && <> · <Day value={chosen.collection_datetime} /></>}
        </p>
        <PlateStartForm item={item} protocols={protocols} t={t} activate={mode === 'activate'}
          onDone={(message) => { onBusyChange?.(false); onDone(message, chosen); }}
          onCancel={() => { setChosen(null); setMode(null); }} />
      </div>
    );
  }

  return (
    <div data-testid="field-picker">
      <p style={{ ...S.hint, margin: '0 0 10px' }}>{t('add.field.existingHint')}</p>
      {state === 'loading' && <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />}
      {state === 'failed' && <p role="alert" style={S.error}>{t('add.field.loadFailed')} {error}</p>}
      {state === 'ready' && rows.length === 0 && <p style={S.hint}>{t('add.field.none')}</p>}
      {state === 'ready' && rows.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }} data-testid="field-picker-list">
          {rows.map((row) => (
            <li key={row.sample_id}>
              <button type="button" onClick={() => pick(row)} data-testid={`field-picker-${row.sample_id}`}
                aria-pressed={chosen?.sample_id === row.sample_id}
                style={{ ...rowStyle, borderColor: chosen?.sample_id === row.sample_id ? 'var(--c-primary)' : 'var(--c-border)' }}>
                {row.thumbnail_url
                  ? <img src={resolvePhotoUrl(row.thumbnail_url)} alt="" style={thumb} />
                  : <span style={{ ...thumb, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} aria-hidden="true">📷</span>}
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'start', minWidth: 0 }}>
                  <span><Code strong>{label(row)}</Code>{row.is_draft ? <span style={S.hint}> · {t('add.field.draftTag')}</span> : null}</span>
                  <span style={S.hint}>
                    {row.current_taxon_name ? <Taxon name={row.current_taxon_name} /> : t('add.field.unnamed')}
                    {row.collection_datetime && <> · <Day value={row.collection_datetime} /></>}
                    {row.photo_count != null && <> · {t('add.field.photos', { count: row.photo_count })}</>}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {chosen && mode === 'checking' && <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />}
      {chosen && mode === 'draft' && (
        <div style={note} data-testid="field-picker-draft">
          <p style={{ margin: 0 }}>{t('add.field.draft')}</p>
          <button type="button" style={S.ghostBtn} onClick={() => onOpenIsolate?.(null, '/field/gallery')}>{t('add.field.openGallery')}</button>
        </div>
      )}
      {chosen && mode === 'has_plates' && (
        <div style={note} data-testid="field-picker-has-plates">
          <p style={{ margin: 0 }}>{t('add.field.hasPlates', { codes: plates.map((p) => p.code || p.plate_code).filter(Boolean).join(', ') })}</p>
          <button type="button" style={S.ghostBtn} onClick={() => onOpenIsolate?.(label(chosen))}
            data-testid="field-picker-open-isolate">{t('add.field.openIsolate')}</button>
        </div>
      )}
      {chosen && mode === 'failed' && <p role="alert" style={S.error}>{error}</p>}
      <div style={{ marginTop: 10 }}>
        <button type="button" style={S.ghostBtn} onClick={onCancel}>{t('common:cancel')}</button>
      </div>
    </div>
  );
}

const rowStyle = {
  display: 'flex', gap: 10, alignItems: 'center', width: '100%', padding: 8, borderRadius: 10, cursor: 'pointer',
  font: 'inherit', color: 'var(--c-text-fg)', background: 'var(--c-surface)', border: '2px solid var(--c-border)',
  boxSizing: 'border-box',
};
const thumb = { width: 56, height: 42, objectFit: 'cover', borderRadius: 6, flexShrink: 0, background: 'var(--c-primary-soft-3)' };
const note = { marginTop: 10, padding: '8px 12px', borderRadius: 8, background: 'var(--c-primary-soft-3)', border: '1px solid var(--c-border)',
  display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' };
